import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  styled,
} from '@mui/material'
import { AnswerExistance } from 'pages/tender-participation/lots/map-to-form-values'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ITenderProtocolData } from 'types/final-protocol'

const StyledTableCell = styled(TableCell)(() => ({
  padding: 0,
  width: '100%',

  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    borderRadius: 0,
  },

  '& .MuiInputBase-root.MuiOutlinedInput-root.Mui-disabled': {
    background: 'rgba(128,128,128, 0.1)',
  },
}))

type Props = {
  protocolData: ITenderProtocolData
}

export const DocRequirementEligibilityEvaluationTable = ({
  protocolData,
}: Props) => {
  const { t } = useTranslation()
  const { control } = useFormContext()

  const { fields } = useFieldArray({
    control,
    name: `docRequirementEligibilityEvaluation`,
  })

  return (
    <TableContainer>
      <Table
        sx={{
          tableLayout: 'fixed',
          background: '#FFF',
          '& td, th': {
            border: '1px solid #ccc',
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>{t('ordinal')}</TableCell>
            <TableCell>{t('formal-requirement-criteria')}</TableCell>
            <TableCell>{t('terms-conditions-requirements')}</TableCell>
            <TableCell
              colSpan={
                protocolData.docRequirementEligibilityEvaluation.orgNames.length
              }
            >
              {t('supplier-name')}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={3} />
            {protocolData.docRequirementEligibilityEvaluation.orgNames?.map(
              (org, idx) => {
                return (
                  <StyledTableCell key={idx}>
                    <Controller
                      name={`docRequirementEligibilityEvaluation.orgNames.${idx}`}
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} fullWidth variant="outlined" />
                      )}
                    />
                  </StyledTableCell>
                )
              }
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {protocolData.docRequirementEligibilityEvaluation.criteria?.map(
            (item, index) => {
              return (
                <TableRow sx={{ width: 'inherit' }}>
                  <TableCell>{index + 1}</TableCell>
                  <StyledTableCell>
                    <Controller
                      name={`docRequirementEligibilityEvaluation.criteria.${index}.criteria`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          variant="outlined"
                          InputProps={{
                            rows: 2,
                            multiline: true,
                            inputComponent: 'textarea',
                          }}
                        />
                      )}
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    <Controller
                      name={`docRequirementEligibilityEvaluation.criteria.${index}.eligibility`}
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} fullWidth variant="outlined" />
                      )}
                    />
                  </StyledTableCell>
                  {protocolData.docRequirementEligibilityEvaluation.criteria[
                    index
                  ].orgInputs?.map((org, idx) => {
                    return (
                      <StyledTableCell key={idx}>
                        <Controller
                          name={`docRequirementEligibilityEvaluation.criteria.${index}.orgInputs.${idx}`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              value={
                                field.value === AnswerExistance.EXIST ||
                                field.value === AnswerExistance.NOT_EXIST
                                  ? t(`existence.${org}`)
                                  : field.value
                              }
                              fullWidth
                              variant="outlined"
                            />
                          )}
                        />
                      </StyledTableCell>
                    )
                  })}
                </TableRow>
              )
            }
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
